import React, { useState, useRef, useEffect, useMemo } from 'react';
import './phone-input.scss';

interface Country {
    name: string;
    code: string;
    dial: string;
    flag: string;
}

const COUNTRIES: Country[] = [
    { name: 'Nigeria', code: 'NG', dial: '+234', flag: '🇳🇬' },
    { name: 'United Kingdom', code: 'GB', dial: '+44', flag: '🇬🇧' },
    { name: 'United States', code: 'US', dial: '+1', flag: '🇺🇸' },
    { name: 'India', code: 'IN', dial: '+91', flag: '🇮🇳' },
    { name: 'Pakistan', code: 'PK', dial: '+92', flag: '🇵🇰' },
    { name: 'Bangladesh', code: 'BD', dial: '+880', flag: '🇧🇩' },
    { name: 'Ghana', code: 'GH', dial: '+233', flag: '🇬🇭' },
    { name: 'South Africa', code: 'ZA', dial: '+27', flag: '🇿🇦' },
    { name: 'Kenya', code: 'KE', dial: '+254', flag: '🇰🇪' },
    { name: 'Egypt', code: 'EG', dial: '+20', flag: '🇪🇬' },
    { name: 'Canada', code: 'CA', dial: '+1', flag: '🇨🇦' },
    { name: 'Australia', code: 'AU', dial: '+61', flag: '🇦🇺' },
    { name: 'Germany', code: 'DE', dial: '+49', flag: '🇩🇪' },
    { name: 'France', code: 'FR', dial: '+33', flag: '🇫🇷' },
    { name: 'Ireland', code: 'IE', dial: '+353', flag: '🇮🇪' },
    { name: 'Saudi Arabia', code: 'SA', dial: '+966', flag: '🇸🇦' },
    { name: 'UAE', code: 'AE', dial: '+971', flag: '🇦🇪' },
    { name: 'Malaysia', code: 'MY', dial: '+60', flag: '🇲🇾' },
    { name: 'Singapore', code: 'SG', dial: '+65', flag: '🇸🇬' },
    { name: 'Tanzania', code: 'TZ', dial: '+255', flag: '🇹🇿' },
    { name: 'Uganda', code: 'UG', dial: '+256', flag: '🇺🇬' },
    { name: 'Cameroon', code: 'CM', dial: '+237', flag: '🇨🇲' },
    { name: 'Sierra Leone', code: 'SL', dial: '+232', flag: '🇸🇱' },
    { name: 'Zambia', code: 'ZM', dial: '+260', flag: '🇿🇲' },
    { name: 'Zimbabwe', code: 'ZW', dial: '+263', flag: '🇿🇼' },
    { name: 'Sudan', code: 'SD', dial: '+249', flag: '🇸🇩' },
    { name: 'Sri Lanka', code: 'LK', dial: '+94', flag: '🇱🇰' },
    { name: 'Nepal', code: 'NP', dial: '+977', flag: '🇳🇵' },
    { name: 'Philippines', code: 'PH', dial: '+63', flag: '🇵🇭' },
    { name: 'China', code: 'CN', dial: '+86', flag: '🇨🇳' },
];

interface PhoneInputProps {
    value: string;
    onChange: (fullPhone: string) => void;
    required?: boolean;
    id?: string;
}

export function PhoneInput({ value, onChange, required = false, id = 'phone' }: PhoneInputProps) {
    // Parse existing value to extract country code and number
    const parsePhone = (phone: string) => {
        if (!phone) return { country: COUNTRIES[0], number: '' };

        // Try to match a country dial code from the stored value
        const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
        for (const c of sorted) {
            if (phone.startsWith(c.dial)) {
                return { country: c, number: phone.slice(c.dial.length) };
            }
        }
        return { country: COUNTRIES[0], number: phone };
    };

    const parsed = parsePhone(value);
    const [selectedCountry, setSelectedCountry] = useState<Country>(parsed.country);
    const [phoneNumber, setPhoneNumber] = useState(parsed.number);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const filteredCountries = useMemo(() => {
        if (!searchQuery) return COUNTRIES;
        const q = searchQuery.toLowerCase();
        return COUNTRIES.filter(
            c => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q)
        );
    }, [searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isDropdownOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isDropdownOpen]);

    const handleCountrySelect = (country: Country) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
        setSearchQuery('');
        onChange(`${country.dial}${phoneNumber}`);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const num = e.target.value.replace(/[^0-9]/g, '');
        setPhoneNumber(num);
        onChange(`${selectedCountry.dial}${num}`);
    };

    return (
        <div className="phone-input-container" ref={dropdownRef}>
            <div className="phone-input-wrapper">
                <button
                    type="button"
                    className="country-selector"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-label="Select country"
                >
                    <span className="country-flag">{selectedCountry.flag}</span>
                    <span className="country-dial">{selectedCountry.dial}</span>
                    <span className="dropdown-arrow">▾</span>
                </button>
                <input
                    type="tel"
                    id={id}
                    className="phone-number-input"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="8012345678"
                    required={required}
                />
            </div>

            {isDropdownOpen && (
                <div className="country-dropdown">
                    <div className="search-box">
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Search country..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="country-search"
                        />
                    </div>
                    <ul className="country-list">
                        {filteredCountries.map((country) => (
                            <li
                                key={country.code}
                                className={`country-option ${country.code === selectedCountry.code ? 'selected' : ''}`}
                                onClick={() => handleCountrySelect(country)}
                            >
                                <span className="country-flag">{country.flag}</span>
                                <span className="country-name">{country.name}</span>
                                <span className="country-dial">{country.dial}</span>
                            </li>
                        ))}
                        {filteredCountries.length === 0 && (
                            <li className="no-results">No countries found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
